import { Component, Injector, OnDestroy, ViewChild, ViewEncapsulation } from '@angular/core';
import {
    DateFormat,
    DownloadService,
    forLifeOf,
    LifecycleAware,
    Modal
} from '@lsq-frontend/common-ui';
import { ResponsiveService } from '@lsq-frontend/common-ui';
import { ModalService } from '@lsq-frontend/common-ui';
import { LsqGroupedDataTableComponent } from '@lsq-frontend/common-ui';
import { of } from 'rxjs';
import { AnalyticsEvent, AnalyticsValue } from '../../../constants/analytics.constants';

import {
    CollectionCheck,
    GetCheckArchiveUrlsRequest,
    GetCheckArchiveUrlsService,
    GetCollectionChecksRequest,
    GetCollectionChecksResponse,
    GetCollectionChecksService,
    GetCollectionInvoicesByRangeService,
    ImageViewableStatus,
    ListCheckKeysRequest,
    ListCheckKeysService
} from '@lsq-frontend/remote-services/enterprise/dashboard-services';
import { DateRangeReportComponent } from '../base/date-range-report.component';
import { ReportsUserStateTypes } from '../reports.user-state-type';
import { FileType } from '@lsq-frontend/remote-services/enterprise/common';
import { LsqApiServiceListSource } from '@lsq-frontend/angular-shared/api-service';
import { AppSettings } from '@lsq-frontend/fasttrack-shared/environment';
import { AnalyticsService } from '@lsq-frontend/angular-shared/features/analytics';
import { AlertService } from '@lsq-frontend/angular-shared/features/alert-service';

@LifecycleAware()
@Component({
    selector: 'collections-report',
    template: `
        <div *ngIf="dataSource" class="min-page-width-medium-up collections-report">
            <lsq-banner
                [titleText]="title"
                [subTitle]="(dataSource.data$ | async)?.lastUpdatedDate | lastUpdatedDate"
                [hasContent]="true"
                [reportName]="title"
                [analyticsName]="analyticsName"
                [service]="getCollectionInvoicesByRangeService"
                [hideExport]="dataSource.noData$ | async"
                [datePickerConfiguration]="datePickerConfiguration"
            >
                <lsq-button
                    *ngIf="rowsChecked.length && !(responsiveService.smBelow | async)"
                    class="check-download-btn"
                    [submitting]="downloadingChecks"
                    (buttonClick)="handleDownloadChecks()"
                >
                    Download Checks ({{ rowsChecked.length }})
                </lsq-button>
            </lsq-banner>
            <div id="collections-report-page-container" class="container-fluid page-container">
                <div class="table">
                    <collections-grouped-table
                        #tableComponent
                        [dataSource]="dataSource"
                        [groupBy]="groupCollectionsBy"
                        [rowPrimaryKey]="'checkKey'"
                        [rowsAreCheckable]="true"
                        [getKeysByDateBaseRequest]="listCheckKeysRequest"
                        [getKeysByDateService]="listCheckKeysService"
                        (requestChange)="dataSource.dispatchRequest($event)"
                        (rowCheck)="handleRowCheck($event)"
                    >
                    </collections-grouped-table>
                </div>
            </div>
        </div>

        <ng-template #checkErrorMessage let-ctx>
            This check bounced for the batch collected on
            {{ ctx.paymentDate | date: dateFormat.LsqShortYear }}
        </ng-template>
    `,
    styleUrls: ['./collections.report.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class CollectionsReportComponent extends DateRangeReportComponent implements OnDestroy {
    @ViewChild('tableComponent') public tableComponent: LsqGroupedDataTableComponent;

    public title = 'Collections';
    public userStateType = ReportsUserStateTypes.GetCollectionChecksRequest;
    public analyticsName = AnalyticsValue.Collections;
    public savedDateFilterName = null;

    public groupCollectionsBy = 'paymentDate';
    public rowsChecked: string[] = [];
    public downloadingChecks = false;
    public downloadTimeout: any;
    public listCheckKeysRequest = new ListCheckKeysRequest();

    // Constants.
    public dateFormat = DateFormat;

    public dataSource = new LsqApiServiceListSource<
        GetCollectionChecksResponse,
        GetCollectionChecksRequest,
        CollectionCheck[]
    >(this.apiService);

    constructor(
        private _appSettings: AppSettings,
        private _getCheckArchiveUrlsService: GetCheckArchiveUrlsService,
        private _downloadService: DownloadService,
        private _alertService: AlertService,
        public analyticsService: AnalyticsService,
        public apiService: GetCollectionChecksService,
        public modalService: ModalService,
        public responsiveService: ResponsiveService,
        public getCollectionInvoicesByRangeService: GetCollectionInvoicesByRangeService,
        public listCheckKeysService: ListCheckKeysService,
        public injector: Injector
    ) {
        super(injector);

        this.dispatchInitialRequest();

        this.dataSource.data$
            .pipe(forLifeOf(this))
            .subscribe((response: GetCollectionChecksResponse) => {
                this.getCollectionInvoicesByRangeService.setRequestStream(
                    of(this.dataSource.request)
                );
            });
    }

    public ngOnDestroy(): void {}

    public handleRowCheck(rows?: string[]): void {
        this.rowsChecked = rows;
    }

    public isRowCheckable(row: CollectionCheck): boolean {
        return row.imageViewableStatus !== ImageViewableStatus.NotViewable;
    }

    public handleDownloadChecks(): void {
        if (this.rowsChecked.length > this._appSettings.maximumImageDownloadSize) {
            this.modalService.showModal(
                new Modal({
                    id: 'check-download-too-large',
                    title: 'Too many checks selected',
                    bodyText: `Too many checks are selected; please select less than ${this._appSettings.maximumImageDownloadSize} checks.`,
                    showFooter: true,
                    showOkButton: true,
                    okButtonText: 'Ok',
                    onOkClick: () => {
                        this.modalService.closeModal();
                    }
                })
            );
        } else if (this.rowsChecked.length > this._appSettings.warningImageDownloadSize) {
            this.modalService.showModal(
                new Modal({
                    id: 'check-download-warning',
                    title: 'Download may take a long time',
                    bodyText:
                        'A large amount of checks have been selected. Your download may take a while. Would you like to continue?',
                    showFooter: true,
                    showCancelButton: true,
                    showOkButton: true,
                    okButtonText: 'Continue',
                    onOkClick: () => {
                        this.downloadingChecks = true;
                        this.downloadChecks();
                        this.modalService.closeModal();
                    }
                })
            );
        } else {
            this.downloadingChecks = true;
            this.downloadChecks();
        }
        this.analyticsService.triggerSimpleEvent(AnalyticsEvent.DownloadChecks);
    }

    public async downloadChecks(): Promise<void> {
        this._getCheckArchiveUrlsService
            .executeOnce(
                new GetCheckArchiveUrlsRequest({
                    checkKeys: this.rowsChecked.map((row) =>
                        parseInt(row.substring(row.indexOf('-') + 1))
                    )
                })
            )
            .then((response) => {
                const dateStamp = new Date().getTime();
                response.urls.forEach((url, index) => {
                    this._downloadService.executeUrl(
                        url,
                        FileType.Zip,
                        `checks-${dateStamp}-${index}`
                    );
                });
                clearTimeout(this.downloadTimeout);
                if (!this.downloadingChecks) {
                    // If the dropdown is already closed, show a success toast.
                    this._alertService.success(
                        'Your check images have been successfully generated.'
                    );
                }
                this.downloadingChecks = false;
                this.tableComponent.handleSelectAll(false);
            })
            .catch((error) => {
                // On error no longer show loading indicator.
                this.downloadingChecks = false;
                clearTimeout(this.downloadTimeout);
            });

        // If generating the report takes more than 10 sec, close and show the info toast.
        this.downloadTimeout = setTimeout(() => {
            this._showGeneratingMessage();
            this.downloadingChecks = false;
        }, this._appSettings.downloadBackgroundTimeout);
    }

    private _showGeneratingMessage(): void {
        this._alertService.info(
            'Collecting your check images can take several minutes. You can continue using the application, but refrain from closing/refreshing the page.',
            {
                title: 'Check Images Generating',
                config: {
                    disableTimeOut: true
                }
            }
        );
    }
}
